import React, { useEffect, useMemo, useRef } from 'react'
import propTypes from 'prop-types'
import classNames from 'classnames/bind'
import { ResizeObserver } from '@juggle/resize-observer'

import useUIContext from 'context/ui'

import PlayIcon from 'assets/svg/video-controls-play.inline.svg'

import s from './HeroInlineVideoSection.module.scss'
const cn = classNames.bind(s)

const STAGGER = 0.08
const BASE_DELAY = 0.15
const SUBTITLE_EXTRA_DELAY = 0.1 // subtitle follows highlight word by a beat

export const wordDelay = (index) => `${index * STAGGER + BASE_DELAY}s`

export const getHighlightWordDelay = (title, titleHighlight = 'audience') => {
  const { line1, line2Prefix, highlight } = parseTitle(title, titleHighlight)
  if (!highlight) return wordDelay(0)

  const line1Count = line1.split(' ').filter(Boolean).length
  const line2Count = line2Prefix.split(' ').filter(Boolean).length
  const highlightIndex = line1Count + line2Count

  return `${highlightIndex * STAGGER + BASE_DELAY + SUBTITLE_EXTRA_DELAY}s`
}

export const parseTitle = (title, highlight = 'audience') => {
  const regex = new RegExp(`(${highlight})`, 'i')
  const match = title.match(regex)

  if (!match) {
    return {
      line1: title,
      line2Prefix: '',
      highlight: null,
      line2Suffix: '',
    }
  }

  const before = title.slice(0, match.index).trim()
  const after = title.slice(match.index + match[0].length)
  const forTheMatch = before.match(/^(.*?)\s+(for\s+the\s*)$/i)

  if (forTheMatch) {
    return {
      line1: forTheMatch[1].trim(),
      line2Prefix: `${forTheMatch[2].trim()} `,
      highlight: match[0],
      line2Suffix: after,
    }
  }

  return {
    line1: before,
    line2Prefix: 'for the ',
    highlight: match[0],
    line2Suffix: after,
  }
}

const Word = ({ index, isLast, highlight, children }) => (
  <span className={cn('word')} style={{ '--d': wordDelay(index) }}>
    <span className={cn('wordInner', { highlight })}>
      {children}
      {!isLast && <>&nbsp;</>}
    </span>
  </span>
)

Word.propTypes = {
  index: propTypes.number.isRequired,
  isLast: propTypes.bool,
  highlight: propTypes.bool,
  children: propTypes.node.isRequired,
}

const HeroInlineVideoTitle = ({ title, titleHighlight = 'audience' }) => {
  const phraseRef = useRef(null)
  const isFontLoaded = useUIContext((s) => s.isFontLoaded)

  const { line1, line2Prefix, highlight, line2Suffix } = useMemo(() => parseTitle(title, titleHighlight), [
    title,
    titleHighlight,
  ])

  const line1Words = useMemo(() => line1.split(' ').filter(Boolean), [line1])
  const line2Words = useMemo(() => line2Prefix.split(' ').filter(Boolean), [line2Prefix])

  const line2StartIndex = line1Words.length
  const highlightIndex = line2StartIndex + line2Words.length
  const suffixIndex = highlightIndex + (highlight ? 1 : 0)

  useEffect(() => {
    const phrase = phraseRef.current
    if (!phrase) return

    const updateGradientPositions = () => {
      const phraseRect = phrase.getBoundingClientRect()
      const phraseWidth = phraseRect.width
      if (!phraseWidth) return

      phrase.querySelectorAll(`.${s.wordInner}, .${s.highlight}`).forEach((el) => {
        const rect = el.getBoundingClientRect()
        el.style.setProperty('--gradient-span', `${phraseWidth}px`)
        el.style.setProperty('--gradient-x', `${rect.left - phraseRect.left}px`)
      })
    }

    updateGradientPositions()

    const ro = new ResizeObserver(updateGradientPositions)
    ro.observe(phrase)

    return () => ro.disconnect()
  }, [title, titleHighlight, isFontLoaded, line2Words.length, highlight, line2Suffix])

  return (
    <div>
      {line1Words.length > 0 && (
        <div className={cn('titleLine1')}>
          {line1Words.map((word, index) => (
            <Word key={`l1-${index}`} index={index} isLast={index === line1Words.length - 1}>
              {word}
            </Word>
          ))}
        </div>
      )}
      {(line2Words.length > 0 || highlight) && (
        <div className={cn('titleLine2')}>
          <span className={cn('playIcon')} style={{ '--d': wordDelay(line1Words.length) }} aria-hidden="true">
            <PlayIcon />
          </span>
          <span className={cn('titleLine2Phrase')} ref={phraseRef}>
            {line2Words.map((word, index) => (
              <Word
                key={`l2-${index}`}
                index={line2StartIndex + index}
                isLast={index === line2Words.length - 1 && !highlight && !line2Suffix}
              >
                {word}
              </Word>
            ))}
            {highlight &&
              (() => {
                // If the suffix is purely punctuation (e.g. "."), fuse it into
                // the same word wrapper so there is no &nbsp; or inline-block gap
                // between the highlight word and the period.
                const suffixIsPunctuation = line2Suffix && /^[.,!?;:]/.test(line2Suffix)
                const hasWordSuffix = line2Suffix && !suffixIsPunctuation
                return (
                  <span className={cn('word')} style={{ '--d': wordDelay(highlightIndex) }}>
                    <span className={cn('wordInner')}>
                      <span className={cn('highlight')}>{highlight}</span>
                      {suffixIsPunctuation && line2Suffix}
                      {hasWordSuffix && <>&nbsp;</>}
                    </span>
                  </span>
                )
              })()}
            {line2Suffix && !/^[.,!?;:]/.test(line2Suffix) && (
              <Word index={suffixIndex} isLast>
                {line2Suffix}
              </Word>
            )}
          </span>
        </div>
      )}
    </div>
  )
}

HeroInlineVideoTitle.propTypes = {
  title: propTypes.string.isRequired,
  titleHighlight: propTypes.string,
}

export default HeroInlineVideoTitle
